import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { FormattedMessage } from '../../i18n/FormattedMessage';
import AriesSearch from '../../components_2/AriesSearch';
import AriesFilterPanel, {
  AriesFilterGroup,
} from '../../components_2/AriesFilterPanel';
import { CalendarIcon } from '../../components_2/AriesIcons';
import { usePageHeaderActions } from '../../components_2/AriesPageHeader/PageHeaderActionsContext';
import DateMonthPicker from '../../components/DateMonthPicker/DateMonthPicker';

const TYPE_OPTIONS = ['All', 'Inspection', 'Quote', 'Work Order'];
const VIEW_MODES = [
  { mode: 'day', label: 'myJobs.filter.day' },
  { mode: 'week', label: 'myJobs.filter.week' },
  { mode: 'month', label: 'myJobs.filter.month' },
];

const isMobileWidth = () =>
  globalThis.window !== undefined && globalThis.window.innerWidth <= 767;

const populateInitialValues = (filtersLabels, isKey) => {
  const values = isKey
    ? Object.keys(filtersLabels ?? {})
    : Object.values(filtersLabels ?? {});
  return values.reduce((filters, key) => {
    filters[key] = false;
    return filters;
  }, {});
};

const setAll = (map, value) =>
  Object.keys(map).reduce((next, key) => {
    next[key] = value;
    return next;
  }, {});

const TaskListHeaderAries = (props) => {
  const {
    searchText,
    isCrossVisible,
    setSearchText,
    handleSearch,
    reportType,
    setReportType,
    setFilters,
    setDatePeriod,
    printPage,
    reportCategories,
    minDate,
    maxDate,
    viewMode,
    setViewMode,
    dateNavigator,
    extraActions,
    hideTypeFilter,
  } = props;

  const [reportFilters, setReportFilters] = useState(() =>
    populateInitialValues(reportCategories, true)
  );
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isDateShowing, setIsDateShowing] = useState(false);
  const [startDatePersist, setStartDatePersist] = useState(new Date());
  const [isMobile, setIsMobile] = useState(isMobileWidth());
  const dateArea = useRef(null);

  useEffect(() => {
    setReportFilters(populateInitialValues(reportCategories, true));
  }, [reportCategories]);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileWidth());
    globalThis.window.addEventListener('resize', handleResize);
    return () => globalThis.window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (dateArea.current?.contains(e.target)) {
        return;
      }
      setIsDateShowing(false);
    };
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  const persistDate = (date) => {
    if (!date) return;
    const start = moment(date).startOf('month');
    const end = moment(date).endOf('month');
    setStartDatePersist(start);
    setIsDateShowing(false);
    setDatePeriod(start, end);
  };

  const resetDate = () => {
    const start = moment().startOf('day');
    const end = moment().endOf('day');
    setStartDatePersist(start);
    setIsDateShowing(false);
    setDatePeriod(start, end);
  };

  const toggleReport = (value) =>
    setReportFilters((prev) => ({ ...prev, [value]: !prev[value] }));

  const applyFilters = () => {
    setFilters({ statusFilters: {}, reportFilters });
    setIsFilterOpen(false);
  };

  const resetFilters = () => {
    const resetReports = populateInitialValues(reportCategories, true);
    setReportFilters(resetReports);
    setIsFilterOpen(false);
    setReportType('');
    setFilters({ statusFilters: {}, reportFilters: resetReports });
  };

  const filterPanel = (
    <AriesFilterPanel
      isOpen={isFilterOpen}
      onToggle={() => setIsFilterOpen((prev) => !prev)}
      onClose={() => setIsFilterOpen(false)}
      footer={
        <div className="aries-filter-actions">
          <button
            type="button"
            className="aries-filter-reset"
            onClick={resetFilters}
          >
            <FormattedMessage id="Reset" defaultMessage="Reset" />
          </button>
          <button
            type="button"
            className="aries-filter-apply"
            onClick={applyFilters}
          >
            <FormattedMessage id="Apply Filter" defaultMessage="Apply Filter" />
          </button>
        </div>
      }
    >
      {!hideTypeFilter && (
        <AriesFilterGroup label="Type">
          <div className="aries-filter-group-list">
            {TYPE_OPTIONS.map((option) => {
              const selected =
                (reportType === '' ? 'All' : reportType) === option;
              return (
                <label key={option} className="aries-filter-group-option">
                  <input
                    type="radio"
                    name="jobs-due-type"
                    checked={selected}
                    onChange={() =>
                      setReportType(option === 'All' ? '' : option)
                    }
                  />
                  <FormattedMessage id={option} defaultMessage={option} />
                </label>
              );
            })}
          </div>
        </AriesFilterGroup>
      )}

      <AriesFilterGroup
        label="Report Category"
        options={Object.keys(reportFilters)}
        values={reportFilters}
        onToggle={toggleReport}
        onSelectAll={() => setReportFilters(setAll(reportFilters, true))}
        onClearAll={() => setReportFilters(setAll(reportFilters, false))}
        showSelectAll
      />
    </AriesFilterPanel>
  );

  // Mobile: the Items Needed badge (if any) + the filter funnel sit on the
  // AriesPageHeader title line (Items Needed left of the funnel), mirroring
  // Team's "+" add action. On web they stay in the search row.
  // Depend on the state that actually changes the registered node (NOT the
  // freshly-created JSX, which is a new object every render and would re-run
  // the effect forever -> setState loop).
  const mobileHeaderActions = isMobile ? (
    <div className="aries-joblist-mobile-actions">
      {extraActions}
      {filterPanel}
    </div>
  ) : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  usePageHeaderActions(mobileHeaderActions, [
    isMobile,
    isFilterOpen,
    reportType,
    reportFilters,
  ]);

  return (
    <div className="aries-sr-header aries-list-header aries-joblist-header">
      <div className="aries-joblist-searchrow">
        <AriesSearch
          searchValue={searchText}
          isCrossVisible={isCrossVisible}
          onChange={(e) => {
            const value = e?.target?.value ?? '';
            if (value === '') {
              handleSearch('');
            } else {
              setSearchText(value);
            }
          }}
          onSearch={() => handleSearch(searchText)}
        />

        {!isMobile && extraActions}

        {!isMobile && filterPanel}
      </div>

      <div className="aries-joblist-controls">
        <div className="aries-joblist-viewmodes">
          {VIEW_MODES.map(({ mode, label }) => (
            <button
              key={mode}
              type="button"
              className={`aries-joblist-viewmode${
                viewMode === mode ? ' is-active' : ''
              }`}
              onClick={() => setViewMode?.(mode)}
            >
              <FormattedMessage id={label} defaultMessage={label} />
            </button>
          ))}
        </div>

        <button
          type="button"
          className="aries-joblist-export"
          onClick={() => printPage?.()}
        >
          <FormattedMessage id="Export" defaultMessage="Export" />
        </button>

        <div className="aries-joblist-date" ref={dateArea}>
          <button
            type="button"
            className="aries-joblist-date-btn"
            aria-label="Select month"
            onClick={() => setIsDateShowing((prev) => !prev)}
          >
            <CalendarIcon size={20} />
          </button>
          {isDateShowing && (
            <DateMonthPicker
              isShowing={isDateShowing}
              onDismiss={() => setIsDateShowing(false)}
              persistDate={persistDate}
              resetdate={resetDate}
              startDatePersist={startDatePersist}
              endDatePersist={moment(startDatePersist).endOf('month')}
              minDate={minDate ?? undefined}
              maxDate={maxDate ?? undefined}
            />
          )}
        </div>
      </div>

      {dateNavigator && (
        <div className="aries-joblist-navrow">{dateNavigator}</div>
      )}
    </div>
  );
};

TaskListHeaderAries.propTypes = {
  searchText: PropTypes.string,
  isCrossVisible: PropTypes.bool,
  setSearchText: PropTypes.func,
  handleSearch: PropTypes.func,
  reportType: PropTypes.string,
  setReportType: PropTypes.func,
  setFilters: PropTypes.func,
  setDatePeriod: PropTypes.func,
  printPage: PropTypes.func,
  reportCategories: PropTypes.object,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  viewMode: PropTypes.string,
  setViewMode: PropTypes.func,
  dateNavigator: PropTypes.node,
  extraActions: PropTypes.node,
  hideTypeFilter: PropTypes.bool,
};

export default TaskListHeaderAries;
